import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/button"
import { Dialog, DialogContent, DialogTrigger } from "@/shared/ui/dialog"
import { Input } from "@/shared/ui/input"
import { Checkbox } from "@/shared/ui/checkbox"
import { selectUserData } from "@/api/rtk/userSelectors"
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { getSignedUrl, uploadToS3 } from "@/api/imperative/uploads"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select"
import { useGenerateTemplateMutation } from "@/api/rtk/mutationsApi"
import Loader from '@/shared/components/Loader'
import { CreateTemplateUploadZone } from "@/features/profile/templates/CreateTemplateUploadZone"
import { safePathSegment } from "@/shared/lib/stringHelpers"

const ALLOWED_TEMPLATE_FILE_TYPES = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

export function CreateTemplateDialog({ tools, onCreated }) {
  const { t } = useTranslation();
  const user = useSelector(selectUserData);
  const [generateTemplate] = useGenerateTemplateMutation();

  const [createOpen, setCreateOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [toolId, setToolId] = useState("");
  const [file, setFile] = useState(null);
  const [createForOrg, setCreateForOrg] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const resetCreate = () => {
    setTemplateName("");
    setToolId("");
    setFile(null);
    setCreateForOrg(false);
  };

  const handleClose = (open) => {
    if (submitting) return;
    setCreateOpen(open);
    if (!open) resetCreate();
  };

  const setTemplateFile = (next) => {
    if (!next) {
      setFile(null);
      return;
    }
    if (!ALLOWED_TEMPLATE_FILE_TYPES.has(next.type)) {
      toast.error(t("profile.templates.errors.pdfOrDocxOnly"));
      setFile(null);
      return;
    }
    setFile(next);
  };

  const handleFileChange = (e) => {
    const next = e.target.files?.[0] || null;
    setTemplateFile(next);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const next = e.dataTransfer.files?.[0] || null;
    setTemplateFile(next);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  const handleGenerate = async () => {
    if (!templateName.trim() || !toolId || !file) {
      toast.error(t("profile.templates.errors.missingFields"));
      return;
    }
    if (!user?.id) {
      toast.error(t("profile.templates.errors.missingUserId"));
      return;
    }
    setSubmitting(true);
    try {
      const randomId = uuidv4();
      const safeFileName = safePathSegment(file.name);
      const s3Key = `templates/${user.id}/uploads/${randomId}/${safeFileName}`;

      const signedUrl = await getSignedUrl({ id: randomId, name: s3Key, type: "upload" });
      if (!signedUrl) {
        throw new Error("Failed to get upload URL");
      }

      const uploadResult = await uploadToS3({
        id: randomId,
        file,
        signedUrl,
        skipReportErrorUpdate: true,
      });
      if (!uploadResult?.uploadStatus) {
        throw new Error("Failed to upload file");
      }

      const owner_type = user?.is_admin && createForOrg ? "org" : "user";
      const name = templateName.trim();

      try {
        await generateTemplate({
          name,
          toolId,
          owner_type,
          s3Key,
        }).unwrap();
      } catch (error) {
        toast.error('Operation failed. Please try again.');
        throw error;
      }

      onCreated?.(owner_type);
      setCreateOpen(false);
      resetCreate();
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const isGenerateDisabled = submitting || !templateName.trim() || !toolId || !file;

  return (
    <Dialog open={createOpen} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button className="w-full sm:w-auto">{t("profile.templates.create.button")}</Button>
      </DialogTrigger>
      <DialogContent
        className="rounded-[25px] md:h-fit h-full shadow-none"
        addingReport={submitting}
        fileUploading={submitting}
      >
        <div className="w-full">
          <h2 className="text-xl font-semibold">{t("profile.templates.create.title")}</h2>
          <p className="text-sm text-[#71717a] mt-2">{t("profile.templates.create.description")}</p>

          <div className="mt-5 flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4 w-full">
              <div className="flex flex-col gap-2 w-full sm:flex-1">
                <label className="text-sm font-semibold">{t("profile.templates.fields.templateName")}</label>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder={t("profile.templates.fields.templateNamePlaceholder")}
                />
              </div>
              <div className="flex flex-col gap-2 w-full sm:flex-1">
                <label className="text-sm font-semibold">{t("profile.templates.fields.tool")}</label>
                <Select value={toolId || ""} onValueChange={(v) => setToolId(v)}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("profile.templates.fields.selectTool")} />
                  </SelectTrigger>
                  <SelectContent>
                    {tools.map((tool) => (
                      <SelectItem key={tool.id} value={tool.id}>
                        {tool.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <CreateTemplateUploadZone
              file={file}
              submitting={submitting}
              fileInputRef={fileInputRef}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onBrowse={handleBrowse}
              onFileChange={handleFileChange}
            />

            {user?.is_admin ? (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="createForOrg"
                  checked={createForOrg}
                  onCheckedChange={(checked) => setCreateForOrg(Boolean(checked))}
                />
                <label htmlFor="createForOrg" className="text-sm font-semibold cursor-pointer">
                  {t("profile.templates.fields.createForOrganisation")}
                </label>
              </div>
            ) : null}

            <Button disabled={isGenerateDisabled} onClick={handleGenerate}>
              {submitting ? <Loader /> : t("profile.templates.create.generate")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
